import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { getAssignments, getAssignmentShareLink, useUpdateAssignment } from "../api/assignments";
import type { AssignmentResponse } from "../api/types";
import {
    exportAssignmentAsA4Image,
    openAssignmentA4Image,
    prefersImageInNewTab
} from "../assignments/exportAssignmentImages";
import { AssignmentMapModal } from "../assignments/AssignmentMapModal";
import {
    useDistributionRounds,
    useDistributionResponses,
    useCreateDistributionRound
} from "../api/distributionRounds";
import { useKretsMembers, type KretsMember } from "../api/kretsMembers";
import { useMunicipalities } from "../api/map";
import { useSelectedMunicipalityId } from "./selectedMunicipality";
import "./admin-assignments-table-page.css";

/**
 * ISO-datum → yyyy-MM-dd (samma format som sv-SE). Datumdelen klipps ut direkt: via
 * `new Date().toISOString()` hamnar 2026-07-25T00:00+02:00 på föregående dygn i UTC.
 */
const toDateValue = (isoDate?: string): string => isoDate?.slice(0, 10) ?? "";

/** Förslag på namn när en ny utdelning startas, t.ex. "Utdelning augusti 2026". */
const suggestRoundName = (): string => {
    const now = new Date();
    const month = now.toLocaleDateString("sv-SE", { month: "long" });
    return `Utdelning ${month} ${now.getFullYear()}`;
};

const canDeliverLabel = (canDeliver?: boolean | null): string =>
    canDeliver === true ? "Ja" : canDeliver === false ? "Nej" : "–";

/** Dagens datum i lokal tid — sv-SE ger redan yyyy-MM-dd, så strängjämförelse räcker. */
const today = new Date().toLocaleDateString("sv-SE");

/**
 * Radens status sett från den som kör ut materialet: vad kan jag göra med den här raden nu?
 * Ordningen är medveten — utlämnat material trumfar allt annat.
 */
type RowStatus = "delivered" | "ready" | "waiting" | "needsDate" | "declined" | "noAnswer";

const rowStatus = (
    materialReceived: boolean | undefined,
    canDeliver: boolean | null | undefined,
    homeFrom: string
): RowStatus => {
    if (materialReceived) return "delivered";
    if (canDeliver === false) return "declined";
    if (canDeliver !== true) return "noAnswer";
    if (!homeFrom) return "needsDate";
    return homeFrom <= today ? "ready" : "waiting";
};

const STATUS_LABELS: Record<RowStatus, string> = {
    delivered: "Material utlämnat",
    ready: "Kan köras ut nu",
    waiting: "Hemma senare",
    needsDate: "Ja, saknar datum",
    declined: "Kan inte dela ut",
    noAnswer: "Inget svar"
};

/**
 * Listan körs i tre steg: jaga svar → dela ut material → klart. Därför en tabell per steg
 * i stället för en enda lång, så det syns direkt hur mycket som återstår i varje steg.
 */
type SectionKey = "noAnswer" | "answered" | "delivered";

const sectionOf = (status: RowStatus): SectionKey =>
    status === "noAnswer" ? "noAnswer" : status === "delivered" ? "delivered" : "answered";

const SECTION_ORDER: { key: SectionKey; title: string; empty: string }[] = [
    { key: "noAnswer", title: "Har inte svarat", empty: "Alla har svarat." },
    {
        key: "answered",
        title: "Har svarat – material ej utlämnat",
        empty: "Inga uppdrag väntar på material."
    },
    { key: "delivered", title: "Har fått material", empty: "Inget material utlämnat än." }
];

/** En färdigtuggad rad: uppdraget plus utdelarens uppgifter och svar för aktiv omgång. */
type TableRow = {
    assignment: AssignmentResponse;
    member?: KretsMember;
    distributorName: string;
    canDeliver?: boolean | null;
    homeFrom: string;
    status: RowStatus;
};

/** Vad som står i fetstil och sorteras på: området eller personen. */
type GroupBy = "area" | "distributor";

const GROUP_BY_STORAGE_KEY = "centerreach.admin.assignmentsTableGroupBy";

const readStoredGroupBy = (): GroupBy => {
    try {
        return window.localStorage.getItem(GROUP_BY_STORAGE_KEY) === "distributor" ? "distributor" : "area";
    } catch {
        return "area";
    }
};

const storeGroupBy = (value: GroupBy): void => {
    try {
        window.localStorage.setItem(GROUP_BY_STORAGE_KEY, value);
    } catch {
        // Blockerad storage — valet gäller bara den här sidvisningen.
    }
};

export const AdminAssignmentsTablePage = (): JSX.Element => {
    const [selectedMunicipalityId, setSelectedMunicipalityId] = useSelectedMunicipalityId();

    const municipalitiesQuery = useMunicipalities({ limit: 2000, includeGeometry: false }, null, { enabled: true });

    const municipalities = useMemo(
        () =>
            (municipalitiesQuery.data?.features ?? [])
                .map((feature) => ({
                    id: String(feature.id ?? feature.properties.municipalityPolygonId),
                    name: feature.properties.name
                }))
                .sort((a, b) => a.name.localeCompare(b.name, "sv")),
        [municipalitiesQuery.data?.features]
    );

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ["assignments", selectedMunicipalityId],
        queryFn: () => getAssignments(undefined, undefined, selectedMunicipalityId),
        enabled: !!selectedMunicipalityId,
        staleTime: 60_000,
        retry: 2
    });

    // Adress och telefon hör till utdelaren (kretsmedlemmen), inte till uppdraget.
    const { data: kretsMembers = [] } = useKretsMembers();
    const kretsMemberById = useMemo(
        () => new Map(kretsMembers.map((member) => [member.id, member])),
        [kretsMembers]
    );

    // Ibland kör man efter område, ibland person för person — därför båda vyerna.
    const [groupBy, setGroupBy] = useState<GroupBy>(() => readStoredGroupBy());

    const getDistributorName = (assignment: AssignmentResponse): string =>
        (assignment.kretsMemberId ? kretsMemberById.get(assignment.kretsMemberId)?.name : undefined) ??
        assignment.kretsMemberName ??
        assignment.distributorName ??
        "";

    const collate = (a: string, b: string): number =>
        a.localeCompare(b, "sv", { numeric: true, sensitivity: "base" });

    const assignments = useMemo(() => {
        const sorted = [...(data ?? [])];
        if (groupBy === "distributor") {
            // Uppdrag utan utdelare hamnar sist i stället för först på en tom sträng.
            return sorted.sort((a, b) => {
                const nameA = getDistributorName(a);
                const nameB = getDistributorName(b);
                if (!nameA !== !nameB) return nameA ? -1 : 1;
                return collate(nameA, nameB) || collate(a.name, b.name);
            });
        }
        return sorted.sort((a, b) => collate(a.name, b.name));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, groupBy, kretsMemberById]);

    // Svaren (kan dela + hemma från) hör till utdelaren och aktiv utdelningsomgång.
    const roundsQuery = useDistributionRounds(selectedMunicipalityId);
    const activeRound = roundsQuery.data?.active;
    const responsesQuery = useDistributionResponses(activeRound?.id);
    const createRoundMutation = useCreateDistributionRound();

    const responseByMemberId = useMemo(
        () => new Map((responsesQuery.data ?? []).map((response) => [response.kretsMemberId, response])),
        [responsesQuery.data]
    );

    const sections = useMemo(() => {
        const rowsBySection: Record<SectionKey, TableRow[]> = { noAnswer: [], answered: [], delivered: [] };

        for (const assignment of assignments) {
            const member = assignment.kretsMemberId ? kretsMemberById.get(assignment.kretsMemberId) : undefined;
            // Svaret hör till utdelaren, så det upprepas på personens alla rader.
            const response = assignment.kretsMemberId
                ? responseByMemberId.get(assignment.kretsMemberId)
                : undefined;
            const homeFrom = toDateValue(response?.homeFromDate);
            const status = rowStatus(assignment.materialReceived, response?.canDeliver, homeFrom);

            rowsBySection[sectionOf(status)].push({
                assignment,
                member,
                distributorName:
                    member?.name ?? assignment.kretsMemberName ?? assignment.distributorName ?? "",
                canDeliver: response?.canDeliver,
                homeFrom,
                status
            });
        }

        return SECTION_ORDER.map((section) => ({
            ...section,
            rows: rowsBySection[section.key],
            totalTargetAddresses: rowsBySection[section.key].reduce(
                (sum, row) => sum + (row.assignment.targetAddressCount ?? 0),
                0
            )
        }));
    }, [assignments, kretsMemberById, responseByMemberId]);

    const updateMutation = useUpdateAssignment();
    const [savingId, setSavingId] = useState<string | null>(null);
    const [exportingId, setExportingId] = useState<string | null>(null);
    // Två lägen: zoombar karta på skärmen, eller nedladdad A4-bild att ta med.
    const [mapAssignment, setMapAssignment] = useState<AssignmentResponse | null>(null);

    const municipalityName = municipalities.find((m) => m.id === selectedMunicipalityId)?.name ?? "";

    const saveField = (id: string, request: Parameters<typeof updateMutation.mutate>[0]["request"]) => {
        setSavingId(id);
        updateMutation.mutate(
            { id, request },
            {
                onSuccess: () => {
                    refetch();
                    // Utlämnat material sätter även "kan dela = ja" på utdelaren, så svaren
                    // måste hämtas om för att kolumnen ska stämma direkt.
                    responsesQuery.refetch();
                    roundsQuery.refetch();
                },
                onError: () => toast.error("Kunde inte spara ändringen."),
                onSettled: () => setSavingId(null)
            }
        );
    };

    /** Ny omgång = nollställning inför nästa utdelning; förra omgångens svar sparas. */
    const handleStartRound = () => {
        if (!selectedMunicipalityId) return;

        const name = window.prompt(
            "Vad heter utdelningen? Utdelarna ser namnet när de svarar.",
            suggestRoundName()
        );
        if (!name?.trim()) return;

        if (
            activeRound &&
            !window.confirm(
                `Starta "${name.trim()}" och avsluta "${activeRound.name}"? Svaren börjar om från blankt – de gamla svaren sparas.`
            )
        ) {
            return;
        }

        createRoundMutation.mutate(
            { municipalityPolygonId: selectedMunicipalityId, name: name.trim() },
            {
                onSuccess: () => toast.success("Ny utdelning startad. Utdelarna kan svara nu."),
                onError: () => toast.error("Kunde inte starta utdelningen.")
            }
        );
    };

    const handleDownloadMap = async (assignment: (typeof assignments)[number]) => {
        setExportingId(assignment.id);
        try {
            // På telefonen öppnas bilden i en ny flik – en nedladdning hamnar bara i galleriet.
            const showImage = prefersImageInNewTab() ? openAssignmentA4Image : exportAssignmentAsA4Image;
            await showImage(assignment);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Kunde inte hämta kartan.");
        } finally {
            setExportingId(null);
        }
    };

    const renderRow = ({ assignment, member, distributorName, canDeliver, homeFrom, status }: TableRow) => {
        const isSaving = savingId === assignment.id;

        return (
            <tr
                key={assignment.id}
                className={`assignments-table__row assignments-table__row--${status}${
                    isSaving ? " assignments-table__row--saving" : ""
                }`}
                title={STATUS_LABELS[status]}
            >
                <td>
                    {groupBy === "distributor" ? (
                        <>
                            <span className="assignments-table__name">
                                {distributorName || "Ingen utdelare"}
                            </span>
                            <span className="assignments-table__distributor">{assignment.name}</span>
                        </>
                    ) : (
                        <>
                            <span className="assignments-table__name">{assignment.name}</span>
                            {distributorName && (
                                <span className="assignments-table__distributor">{distributorName}</span>
                            )}
                        </>
                    )}
                </td>
                <td className="assignments-table__num">{assignment.targetAddressCount ?? "–"}</td>
                <td>{member?.homeAddress?.trim() || "–"}</td>
                <td className="assignments-table__phone">{member?.phoneNumber?.trim() || "–"}</td>
                <td className="assignments-table__check">
                    <span className={`assignments-table__chip assignments-table__chip--${status}`}>
                        {canDeliverLabel(canDeliver)}
                    </span>
                </td>
                <td
                    className={`assignments-table__date-cell${
                        status === "ready" ? " assignments-table__date-cell--ready" : ""
                    }`}
                >
                    {homeFrom || "–"}
                </td>
                <td className="assignments-table__check">
                    <input
                        type="checkbox"
                        className="assignments-table__checkbox assignments-table__screen-only"
                        checked={assignment.materialReceived ?? false}
                        disabled={isSaving}
                        aria-label={`Fått material: ${assignment.name}`}
                        onChange={(event) =>
                            saveField(assignment.id, { materialReceived: event.target.checked })
                        }
                    />
                    <span className="assignments-table__print-only assignments-table__check-print">
                        {assignment.materialReceived ? "✓" : ""}
                    </span>
                </td>
                <td className="assignments-table__screen-only-cell assignments-table__row-actions">
                    <Link
                        to={`/admin/assignments?edit=${assignment.id}`}
                        className="assignments-table__edit-link"
                        title="Öppna uppdraget i kartvyn för redigering"
                    >
                        Redigera
                    </Link>
                    {assignment.area && (
                        <>
                            <button
                                type="button"
                                className="assignments-table__map-button"
                                onClick={() => setMapAssignment(assignment)}
                                title="Visa uppdraget på en zoombar karta"
                            >
                                Karta
                            </button>
                            <button
                                type="button"
                                className="assignments-table__map-button"
                                disabled={exportingId === assignment.id}
                                onClick={() => handleDownloadMap(assignment)}
                                title="Ladda ned A4-karta för det här uppdraget"
                            >
                                {exportingId === assignment.id ? "Hämtar…" : "Bild"}
                            </button>
                        </>
                    )}
                </td>
            </tr>
        );
    };

    // Länken är öppen och skyddas bara av sin token, som hämtas (och skapas vid behov) här.
    const [isCopyingLink, setIsCopyingLink] = useState(false);

    const handleCopyShareLink = async () => {
        if (!selectedMunicipalityId) return;

        setIsCopyingLink(true);
        try {
            const link = await getAssignmentShareLink(selectedMunicipalityId);
            const url = `${window.location.origin}/uppdrag?t=${link.token}`;
            try {
                await navigator.clipboard.writeText(url);
                toast.success("Länk kopierad – skicka den till utdelarna.");
            } catch {
                // Urklipp kan vara blockerat (t.ex. utan https) — visa länken så den går att kopiera för hand.
                window.prompt("Kopiera länken:", url);
            }
        } catch {
            toast.error("Kunde inte hämta utdelarlänken.");
        } finally {
            setIsCopyingLink(false);
        }
    };

    return (
        <div className="assignments-table-page">
            <div className="assignments-table-page__toolbar">
                <div className="assignments-table-page__toolbar-left">
                    <Link to="/admin/assignments" className="assignments-table-page__back-link">
                        ← Uppdrag på karta
                    </Link>
                    <label className="assignments-table-page__municipality">
                        Kommun
                        <select
                            value={selectedMunicipalityId}
                            onChange={(event) => setSelectedMunicipalityId(event.target.value)}
                        >
                            <option value="">Välj kommun</option>
                            {municipalities.map((municipality) => (
                                <option key={municipality.id} value={municipality.id}>
                                    {municipality.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <div className="assignments-table-page__group-by">
                        Sortera på
                        <div className="assignments-table-page__segmented">
                            <button
                                type="button"
                                className={groupBy === "area" ? "is-active" : ""}
                                onClick={() => {
                                    setGroupBy("area");
                                    storeGroupBy("area");
                                }}
                            >
                                Område
                            </button>
                            <button
                                type="button"
                                className={groupBy === "distributor" ? "is-active" : ""}
                                onClick={() => {
                                    setGroupBy("distributor");
                                    storeGroupBy("distributor");
                                }}
                            >
                                Utdelare
                            </button>
                        </div>
                    </div>
                </div>
                <div className="assignments-table-page__toolbar-actions">
                    <button
                        type="button"
                        className="assignments-table-page__share-button"
                        onClick={handleCopyShareLink}
                        disabled={!selectedMunicipalityId || isCopyingLink}
                        title="Öppen länk (utan inloggning) där utdelarna fyller i hemma från-datum"
                    >
                        {isCopyingLink ? "Hämtar länk…" : "Kopiera utdelarlänk"}
                    </button>
                    <button
                        type="button"
                        className="assignments-table-page__share-button"
                        onClick={handleStartRound}
                        disabled={!selectedMunicipalityId || createRoundMutation.isPending}
                        title="Nollställ inför nästa utdelning – svaren börjar om, de gamla sparas"
                    >
                        Ny utdelning
                    </button>
                    <button
                        type="button"
                        className="assignments-table-page__print-button"
                        onClick={() => window.print()}
                        disabled={!selectedMunicipalityId || assignments.length === 0}
                    >
                        Skriv ut
                    </button>
                </div>
            </div>

            <h1 className="assignments-table-page__title">
                Utdelningslista{municipalityName ? ` – ${municipalityName}` : ""}
            </h1>

            {selectedMunicipalityId && (
                <p className="assignments-table-page__round">
                    {activeRound ? (
                        <>
                            <strong>{activeRound.name}</strong> · {activeRound.yesCount} ja ·{" "}
                            {activeRound.noCount} nej
                        </>
                    ) : (
                        <>Ingen utdelning startad – starta en för att kunna samla in svar.</>
                    )}
                </p>
            )}

            {assignments.length > 0 && (
                <ul className="assignments-table-page__legend">
                    {(["ready", "waiting", "needsDate", "noAnswer", "declined", "delivered"] as const).map(
                        (status) => (
                            <li key={status}>
                                <span className={`assignments-table__dot assignments-table__dot--${status}`} />
                                {STATUS_LABELS[status]}
                            </li>
                        )
                    )}
                </ul>
            )}

            {!selectedMunicipalityId && (
                <p className="assignments-table-page__hint">Välj kommun för att visa uppdragen.</p>
            )}
            {selectedMunicipalityId && isLoading && <p className="assignments-table-page__hint">Laddar uppdrag…</p>}
            {selectedMunicipalityId && isError && (
                <p role="alert" className="assignments-table-page__error">
                    Kunde inte hämta uppdrag.{" "}
                    <button type="button" onClick={() => refetch()}>
                        Försök igen
                    </button>
                </p>
            )}
            {selectedMunicipalityId && !isLoading && !isError && assignments.length === 0 && (
                <p className="assignments-table-page__hint">Inga uppdrag i vald kommun.</p>
            )}

            {assignments.length > 0 &&
                sections.map((section) => (
                    <section key={section.key} className="assignments-table-page__section">
                        <h2 className="assignments-table-page__section-title">
                            {section.title}{" "}
                            <span className="assignments-table-page__section-count">
                                ({section.rows.length})
                            </span>
                        </h2>

                        {section.rows.length === 0 ? (
                            <p className="assignments-table-page__hint">{section.empty}</p>
                        ) : (
                            <table className="assignments-table">
                                <thead>
                                    <tr>
                                        <th>Namn</th>
                                        <th className="assignments-table__num">Antal</th>
                                        <th>Adress</th>
                                        <th>Telefon</th>
                                        <th className="assignments-table__check">Kan dela</th>
                                        <th>Hemma från</th>
                                        <th className="assignments-table__check">Fått material</th>
                                        <th className="assignments-table__screen-only-cell" />
                                    </tr>
                                </thead>
                                <tbody>{section.rows.map(renderRow)}</tbody>
                                <tfoot>
                                    <tr>
                                        <td>Totalt ({section.rows.length} uppdrag)</td>
                                        <td className="assignments-table__num">
                                            {section.totalTargetAddresses}
                                        </td>
                                        <td colSpan={5} />
                                        <td className="assignments-table__screen-only-cell" />
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </section>
                ))}

            {mapAssignment && (
                <AssignmentMapModal
                    assignment={mapAssignment}
                    onClose={() => setMapAssignment(null)}
                    onDownload={() => handleDownloadMap(mapAssignment)}
                    isDownloading={exportingId === mapAssignment.id}
                />
            )}
        </div>
    );
};

export default AdminAssignmentsTablePage;
